import React, { useState, useEffect } from 'react';
import { makeStyles } from '@mui/styles';
import { capitalize } from '@mui/material/utils';
import {
  Grid,
  Box,
  Table,
  TableBody,
  TableRow,
  Typography,
  Button
} from '@mui/material';
import ErrorIcon from '@mui/icons-material/Error';
import { useNavigate } from 'react-router';
import { formDataInstance } from '@/common/axios';
import CONFIG from '@/config';
import { getRestHeaders } from '@/common/utils/api';
import CardLoader from '@/components/CardLoader';
import moment from 'moment';
import PatientUpdateConfirmModal from '../../../AdvancedMDLink/PatientUpdateConfirmModal';
import { useSnackbar } from 'notistack';
import {
  AMDLinkStyles,
  AMDLinkTableCell,
  AMDLinkCard
} from '../../../AdvancedMDLink/index';
import { capitalizeAll } from '@/common/utils/utils';

const useStyles: any = makeStyles(() => ({
  headingText: {
    fontWeight: 700,
    fontSize: '1.25em',
    marginBottom: '10px'
  },
  bodyText: {
    fontSize: '14px'
  }
}));

function AdvancedMDTabs(props: any) {
  const { customerAllData } = props;
  const classes = useStyles();
  const linkClasses = AMDLinkStyles();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [loadingLinkStatus, setLoadingLinkStatus] = useState(false);
  const [loadingAMDEncounters, setLoadingAMDEncounters] = useState(false);
  const [loadingPatientUpdate, setLoadingPatientUpdate] = useState(false);
  const [linkData, setLinkData] = useState(null);
  const [AMDEncounters, setAMDEncounters] = useState([]);
  const [isUpdatePatientModalOpen, setIsUpdatePatientModalOpen] =
    useState(false);

  const handleLinkNavigation = () => {
    navigate(`/customer/amd/${customerAllData?.id}`, {
      state: {
        firstName: customerAllData?.firstName,
        lastName: customerAllData?.lastName,
        dob: customerAllData?.dob,
        gender: customerAllData?.gender,
        mobilePhone: customerAllData?.mobilePhone,
        email: customerAllData?.email,
        linkStatus: linkData?.status === 'linked' ? 'linked' : 'not_linked'
      }
    });
  };

  const getLinkStatus = async () => {
    setLoadingLinkStatus(true);
    try {
      const response = await formDataInstance.get(
        CONFIG.MEDICAL_API_URL + '/staff/link-status/',
        {
          params: { customerId: customerAllData?.id },
          headers: getRestHeaders()
        }
      );
      setLinkData(response?.data?.data);
    } catch (error) {
      enqueueSnackbar('Failed to fetch AdvancedMD link status', {
        variant: 'error'
      });
    } finally {
      setLoadingLinkStatus(false);
    }
  };

  const getAMDEncounters = async () => {
    try {
      setLoadingAMDEncounters(true);
      const response = await formDataInstance.get(
        CONFIG.MEDICAL_API_URL + '/staff/amd-medical-encounters/',
        {
          params: { patientId: linkData?.patientId },
          headers: getRestHeaders()
        }
      );
      setAMDEncounters(response?.data?.data);
    } catch (error) {
      enqueueSnackbar('Failed to fetch AdvancedMD encounters', {
        variant: 'error'
      });
    } finally {
      setLoadingAMDEncounters(false);
    }
  };

  const handleUpdatePatient = async () => {
    try {
      setLoadingPatientUpdate(true);
      const response = await formDataInstance.patch(
        CONFIG.MEDICAL_API_URL + '/staff/patients/',
        {
          customerId: customerAllData?.id
        },
        {
          headers: getRestHeaders()
        }
      );
      setLinkData({
        ...response?.data?.data,
        status: linkData.status,
        wasAutoLinked: linkData.wasAutoLinked
      });
      enqueueSnackbar('Patient updated successfully', {
        variant: 'success'
      });
    } catch (error) {
      enqueueSnackbar('Failed to update patient', {
        variant: 'error'
      });
    } finally {
      setLoadingPatientUpdate(false);
      setIsUpdatePatientModalOpen(false);
    }
  };

  useEffect(() => {
    getLinkStatus();
  }, []);

  useEffect(() => {
    if (linkData?.status === 'linked') {
      getAMDEncounters();
    }
  }, [linkData]);

  return (
    <div>
      <Grid item xs={12}>
        <Box p={1}>
          <Typography className={classes.headingText} color="primary">
            AdvancedMD Link
          </Typography>

          {loadingLinkStatus ? (
            <CardLoader />
          ) : linkData ? (
            linkData.status === 'not_linked' ? (
              <>
                <Typography className={classes.bodyText} variant="body2">
                  No AdvancedMD profile linked
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  sx={{ mt: 1 }}
                  onClick={() => handleLinkNavigation()}
                >
                  Link AdvancedMD profile
                </Button>
              </>
            ) : (
              <AMDLinkCard sx={{ mt: 2, p: 2, width: '50%' }} elevation={0}>
                <Grid container>
                  <Grid item xs={6}>
                    <Typography
                      className={linkClasses.amdNameText}
                      sx={{ mb: 2 }}
                    >
                      {linkData.firstName} {linkData.lastName}
                    </Typography>
                    <Table sx={{ mb: 2 }}>
                      <TableBody>
                        <TableRow>
                          <AMDLinkTableCell align="left">
                            <Box display="flex" alignItems="center">
                              <Typography
                                className={linkClasses.strongText}
                                style={{ marginRight: '4px' }}
                              >
                                DOB:
                              </Typography>
                              <Typography className={linkClasses.text}>
                                {linkData.dob}
                              </Typography>
                            </Box>
                          </AMDLinkTableCell>
                        </TableRow>
                        <TableRow>
                          <AMDLinkTableCell align="left">
                            <Box display="flex" alignItems="center">
                              <Typography
                                className={linkClasses.strongText}
                                style={{ marginRight: '4px' }}
                              >
                                Sex:
                              </Typography>
                              <Typography className={linkClasses.text}>
                                {linkData.sex}
                              </Typography>
                            </Box>
                          </AMDLinkTableCell>
                        </TableRow>
                        <TableRow>
                          <AMDLinkTableCell align="left">
                            <Box display="flex" alignItems="center">
                              <Typography
                                className={linkClasses.strongText}
                                style={{ marginRight: '4px' }}
                              >
                                Phone:
                              </Typography>
                              <Typography className={linkClasses.text}>
                                {capitalize(linkData.phone)}
                              </Typography>
                            </Box>
                          </AMDLinkTableCell>
                        </TableRow>
                        <TableRow>
                          <AMDLinkTableCell align="left">
                            <Box display="flex" alignItems="center">
                              <Typography
                                className={linkClasses.strongText}
                                style={{ marginRight: '4px' }}
                              >
                                Email:
                              </Typography>
                              <Typography className={linkClasses.text}>
                                {linkData.email?.toLowerCase()}
                              </Typography>
                            </Box>
                          </AMDLinkTableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </Grid>
                  <Grid item xs={6}>
                    <Box
                      display="flex"
                      flexDirection="column"
                      alignItems="flex-end"
                    >
                      <Button
                        variant="contained"
                        color="primary"
                        style={{ marginBottom: '8px' }}
                        onClick={handleLinkNavigation}
                      >
                        Relink AdvancedMD profile
                      </Button>
                      <Button
                        variant="outlined"
                        color="primary"
                        onClick={() => setIsUpdatePatientModalOpen(true)}
                      >
                        Update AdvancedMD profile
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
                <Typography className={linkClasses.strongText} sx={{ mb: 0.5 }}>
                  Recent AdvancedMD encounters
                </Typography>
                {loadingAMDEncounters ? (
                  <div style={{ marginTop: '40px' }}>
                    <CardLoader />
                  </div>
                ) : AMDEncounters.length > 0 ? (
                  AMDEncounters.map((encounter, index) => {
                    return (
                      <AMDLinkCard
                        key={index}
                        sx={{ mt: 2, p: 2 }}
                        elevation={0}
                      >
                        <Typography className={linkClasses.lightText}>
                          {moment(encounter?.timestamp).format('MM/DD/YYYY')}
                        </Typography>
                        <Typography className={linkClasses.lightText}>
                          {moment(encounter?.timestamp).format('h:mm A')}
                        </Typography>
                        <Typography
                          className={linkClasses.lightText}
                          sx={{ mb: 1 }}
                        >
                          {encounter?.storeDescription}
                        </Typography>
                        <Typography
                          className={linkClasses.strongText}
                          sx={{ mb: 1 }}
                        >
                          {capitalizeAll(encounter?.type.replace(/_/g, ' '))}
                        </Typography>
                        {encounter?.purchasableItems &&
                          encounter?.purchasableItems.map((item, i) => (
                            <Typography key={i} className={linkClasses.text}>
                              {item?.description}
                            </Typography>
                          ))}
                        {encounter?.type == 'medical_call' && (
                          <>
                            <Typography className={linkClasses.text}>
                              <b>Contact Purpose:</b>{' '}
                              {encounter?.contactPurpose}
                            </Typography>
                            <Typography className={linkClasses.text}>
                              <b>Contact Type:</b> {encounter?.contactType}
                            </Typography>
                            <Typography className={linkClasses.text}>
                              <b>Nurse Practitioner:</b>{' '}
                              {encounter?.nursePractitionerName}
                            </Typography>
                          </>
                        )}
                      </AMDLinkCard>
                    );
                  })
                ) : (
                  <Typography className={linkClasses.lightText}>
                    No AMD encounters found.
                  </Typography>
                )}
              </AMDLinkCard>
            )
          ) : (
            <Grid
              container
              direction="row"
              justifyContent="center"
              alignItems="center"
            >
              <Box>
                <ErrorIcon style={{ color: '#004f78', fontSize: 60 }} />
              </Box>
            </Grid>
          )}
        </Box>
      </Grid>
      <PatientUpdateConfirmModal
        isOpen={isUpdatePatientModalOpen}
        onClose={() => setIsUpdatePatientModalOpen(false)}
        onConfirm={handleUpdatePatient}
        loading={loadingPatientUpdate}
      />
    </div>
  );
}

export default AdvancedMDTabs;
